import { $log } from '../y/debug';
import { $ui } from './functions';
import { UITouchDelegate, UITouchEvent } from './touch';
import { UIView, UIViewSpec } from './view';

export interface UIDropDelegate {
    dropEnter?(content: UIContentPreview): boolean;
    dropLeave?(): void;
    drop(content: UIContent): void | Promise<boolean> | boolean;
}

export interface UIDragDelegate {
    dragStart(): [UIContent, UIViewSpec] | undefined;
    dragDrop?(): void | boolean;
    dragCancel?(): void;
}

declare global {
    interface Element {
        ui_drop_delegate?: UIDropDelegate;
    }
}

export interface UIContentTypes {
    number: [number, number];
    color: [string, string];
    files: [undefined | File[], File[]];
}

export type UIContent = { [K in keyof UIContentTypes]: { type: K, value: UIContentTypes[K][1] } }[keyof UIContentTypes]
export type UIContentPreview = { [K in keyof UIContentTypes]: { type: K, value: UIContentTypes[K][0] } }[keyof UIContentTypes]

const IMAGE_MIMETYPE = /^image\//;
export function isImageMime(mime: string) {
    return IMAGE_MIMETYPE.test(mime);
}

function onEnter(target: Element, content: UIContentPreview) {
    if(target.ui_drop_delegate?.dropEnter?.(content)) {
        target.classList.add('x-drop-inside');
        return true;
    }
    return false;
}

function onLeave(target: Element) {
    target.classList.remove('x-drop-inside');
    target.ui_drop_delegate?.dropLeave?.();
}

function onDrop(target: Element, content: UIContent) {
    return target.ui_drop_delegate?.drop(content);
}

let over_target: Element | null;
let over_target_ok: boolean;
let over_element: Element | undefined;
let enter_timestamp: any;

function handleDragOver(e: DragEvent) {
    const transfer = e.dataTransfer;
    if(!transfer?.types?.includes('Files')) return;

    e.preventDefault();
    if(e.target != over_element) {
        over_element = e.target as Element;
        const target = over_element.closest('.x-drop');
        if(target != over_target) {
            if(over_target && over_target_ok)
                onLeave(over_target);
            if(target)
                over_target_ok = onEnter(target, { type: 'files', value: undefined });
            over_target = target;
        }
    }

    transfer.dropEffect = over_target && over_target_ok ? 'copy' : 'none';
};

document.addEventListener('dragenter', e => {
    handleDragOver(e);
    enter_timestamp = e.timeStamp;
});

document.addEventListener('dragleave', e => {
    e.preventDefault();
    if(enter_timestamp == e.timeStamp) return;
    // TODO: check if we really need the timestamp or relatedTarget works all the time
    if(e.relatedTarget) return;
    over_element = undefined;
    if(over_target && over_target_ok)
        onLeave(over_target);
    over_target = null;
});

document.addEventListener('dragover', handleDragOver);

document.addEventListener('drop', e => {
    e.preventDefault();

    over_element = undefined;
    if(over_target && over_target_ok) {
        if(e.dataTransfer) {
            const files = Array.from(e.dataTransfer.files);
            if(files.length)
                onDrop(over_target, { type: 'files', value: files });
        }
        onLeave(over_target);
    }
    over_target = null;
});

export function uiInitDropTarget(where: UIView & UIDropDelegate): void
export function uiInitDropTarget(where: UIViewSpec, target: UIDropDelegate): void
export function uiInitDropTarget(where: UIViewSpec, target?: UIDropDelegate) {
    const elem = where.ui_elem;
    elem.classList.add('x-drop');
    elem.ui_drop_delegate = target || (where as any);
}

export function uiStartDrag(e: UITouchEvent, delegate: UIDragDelegate, distance: number = 10): UITouchDelegate {
    const touch = e.touch;

    // const start_pos = [touch.x + window.scrollX, touch.y + window.scrollY];
    // let scroll_token: {} | undefined;
    let started = false;
    let content: UIContent;
    let thumbnail: HTMLElement;
    let droppables: Element[];
    let drop_target: Element | undefined;
    let drop_target_ok: boolean;
    let placeholder: Element | undefined;

    const end = (dropped: boolean) => {
        let restore = true;
        if(dropped)
            restore = delegate.dragDrop?.() !== false;
        else
            delegate.dragCancel?.();

        $ui.setGlobalCursor(null);
        // scroll_token = undefined;
        if(placeholder) {
            thumbnail.classList.remove('x-dragging');
            if(restore) {
                $log('app', 'restoring!!!!');
                thumbnail.style.width = '';
                thumbnail.style.height = '';
                thumbnail.style.top = '';
                thumbnail.style.left = '';
                placeholder.replaceWith(thumbnail);
            } else {
                placeholder.remove();
                thumbnail.remove();
            }
        } else {
            thumbnail.remove();
        }
    }

    return {
        touchMove: () => {
            // scroll_token = undefined;

            if(!started) {
                if(Math.sqrt(touch.dx * touch.dx + touch.dy * touch.dy) < distance) return;
                const start = delegate.dragStart();
                if(!start) return false;
                content = start[0];
                thumbnail = start[1].ui_elem;
                started = true;

                if(thumbnail.isConnected) {
                    placeholder = $ui.placehold(thumbnail);
                    thumbnail.replaceWith(placeholder);
                }
                thumbnail.classList.add('x-dragging');
                document.body.ui_add(thumbnail);

                $ui.setGlobalCursor('grabbing');

                droppables = Array.from(document.body.getElementsByClassName('x-drop')).reverse();
                for(const d of droppables) {
                    d.ui_drop_position = d.ui_drop_text_only ? d.ui_docTextRect() : d.ui_docRect();
                }
                drop_target = undefined;
            }

            const x = touch.x + window.scrollX;
            const y = touch.y + window.scrollY;

            const new_target = droppables.find(d => {
                const r = d.ui_drop_position;
                return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
            });

            if(new_target !== drop_target) {
                drop_target && onLeave(drop_target);
                drop_target = new_target;
                drop_target_ok = !!(drop_target && onEnter(drop_target, content));
            }

            // TODO: fix this!!!! (functions for positioning in ui-core)
            thumbnail.style.left = (x - thumbnail.clientWidth / 2) + 'px';
            thumbnail.style.top = (y - thumbnail.clientHeight * 2 / 3) + 'px';

            return true;

            // if(!started) {
            //     delegate.orderStart();
            //     started = true;
            //     $ui.setOverflowAnchor(false);
            // }

            // const offset: UIPoint = [touch.x + window.scrollX - start_pos[0], touch.y + window.scrollY - start_pos[1]];

            // const rect = delegate.orderMove(offset);

            // if(rect.top < 0 && touch.dy < -20) {
            //     const s = -Math.round(Math.pow(Math.min(-touch.dy-20, -rect.top),1/3));
            //     window.scrollBy(0, s);

            //     const token = scroll_token = {};

            //     const callback = () => {
            //         if(token != scroll_token) return;
            //         offset[1] += s;
            //         const rect = delegate.orderMove(offset);
            //         if(rect.top < 0) {
            //             window.scrollBy(0, s);
            //             requestAnimationFrame(callback);
            //         }
            //     }

            //     requestAnimationFrame(callback);
            // } else if(rect.bottom > window.innerHeight && touch.dy > 20) {
            //     const s = Math.round(Math.pow(Math.min(touch.dy-20,rect.bottom - window.innerHeight),1/3));
            //     window.scrollBy(0, s);

            //     const token = scroll_token = {};

            //     const callback = () => {
            //         if(token != scroll_token) return;
            //         offset[1] += s;
            //         const rect = delegate.orderMove(offset);
            //         if(rect.bottom > window.innerHeight) {
            //             window.scrollBy(0, s);
            //             requestAnimationFrame(callback);
            //         }
            //     }

            //     requestAnimationFrame(callback);
            // }

            // return true;
        },
        touchEnd: () => {
            if(started) {
                started = false;
                if(drop_target && drop_target_ok) {
                    const result = onDrop(drop_target, content);
                    if(result instanceof Promise)
                        result.then(r => end(r !== false), () => end(false));
                    else
                        end(result !== false);

                    onLeave(drop_target);
                } else {
                    end(false);
                }
            }

            return true;
        },
        stop: true
    };
}
